import { motion } from "framer-motion"
import { VariantReader } from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { twMerge } from "tailwind-merge"
import { useRef, useState, useEffect, useCallback } from "react"

const ToggleSwitch = ({
  isOn,
  onChange,
  variant = "default",
  styleOverrides,
  localUserPreferencesKey = null,
}) => {
  const variants = VariantReader.useVariant({
    componentName: "ToggleSwitch",
    variant,
    styleOverrides,
  })

  const { container, background, active, inactive, toggle } = variants

  const containerRef = useRef(null)
  const thumbRef = useRef(null)
  const [moveDistance, setMoveDistance] = useState(0)

  const measure = useCallback(() => {
    if (!containerRef.current || !thumbRef.current) return
    const cw = containerRef.current.offsetWidth
    const tw = thumbRef.current.offsetWidth
    const tl = thumbRef.current.offsetLeft
    if (cw === 0) return
    setMoveDistance(cw - tw - tl * 2)
  }, [])

  useEffect(() => {
    measure()
    const ro = new ResizeObserver(measure)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [variant, measure])

  const dragThreshold = moveDistance / 2

  // a click on the knob is a click, not a drag: framer still ends a "drag" on it, which used
  // to set the value from where the knob sat (off) right after the click turned it on; and a
  // real drag must not be followed by the click it ends with
  const dragged = useRef(false)

  const handleToggle = () => {
    if (dragged.current) {
      dragged.current = false
      return
    }
    const newValue = !isOn
    if (onChange) onChange(newValue)
    if (localUserPreferencesKey?.trim()) {
      try { LocalUserPreferences.set(localUserPreferencesKey, newValue) } catch {}
    }
  }

  return (
    <div ref={containerRef} className={container} onClick={handleToggle}>
      <motion.div
        className={twMerge(background, isOn ? active : inactive)}
        transition={{ duration: 0.2 }}
      />
      <motion.div
        ref={thumbRef}
        className={toggle}
        drag="x"
        dragConstraints={{ left: 0, right: moveDistance }}
        dragElastic={0}
        onDragEnd={(_, info) => {
          if (Math.abs(info.offset.x) < 4) return
          dragged.current = true
          setTimeout(() => (dragged.current = false), 0)
          const newValue = info.offset.x > dragThreshold
          onChange?.(newValue)
          if (localUserPreferencesKey?.trim()) {
            try { LocalUserPreferences.set(localUserPreferencesKey, newValue) } catch {}
          }
        }}
        animate={{ x: isOn ? moveDistance : 0 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
      />
    </div>
  )
}

export default ToggleSwitch
